import { useMemo } from 'react';
import { calculateWeeksInRange, getProjectStats, getTotalPoints, getCurrentWeek } from '../utils';

/**
 * Hook to calculate all dashboard statistics
 */
export const useStats = (weeklyData, timeRange, projectSettings = {}) => {
  const stats = useMemo(() => {
    const weeks = calculateWeeksInRange(timeRange);
    let totalVolume = 0;
    let totalCosts = 0;
    let totalPoints = 0;
    let totalFees = 0;
    let totalCustomMetric = 0;

    // Calculate stats for all projects in projectSettings
    Object.keys(projectSettings).forEach(projectName => {
      const projectStats = getProjectStats(weeks, weeklyData, projectName);
      totalVolume += projectStats.volume || 0;
      totalCosts += projectStats.cost || 0;
      totalPoints += projectStats.points || 0;
      totalFees += projectStats.fees || 0;
      totalCustomMetric += projectStats.customMetric || 0;
    });

    // If no project settings, calculate from all entries
    if (Object.keys(projectSettings).length === 0) {
      if (!weeks) {
        // All weeks
        Object.values(weeklyData?.weeks || {}).forEach(weekEntries => {
          weekEntries.forEach(entry => {
            totalVolume += entry.volume || 0;
            totalCosts += entry.cost || 0;
            totalPoints += entry.points || 0;
            totalFees += entry.fees || 0;
            totalCustomMetric += entry.customMetric || 0;
          });
        });
      } else {
        weeks.forEach(week => {
          const weekEntries = weeklyData?.weeks?.[week] || [];
          weekEntries.forEach(entry => {
            totalVolume += entry.volume || 0;
            totalCosts += entry.cost || 0;
            totalPoints += entry.points || 0;
            totalFees += entry.fees || 0;
            totalCustomMetric += entry.customMetric || 0;
          });
        });
      }
    }
    
    return { totalVolume, totalCosts, totalPoints, totalFees, totalCustomMetric };
  }, [weeklyData, timeRange, projectSettings]);

  // Weekly progress - uses current UTC week
  const weeklyProgress = useMemo(() => {
    const currentWeek = getCurrentWeek();
    const entries = weeklyData?.weeks?.[currentWeek] || [];
    
    const progress = {};
    
    // Initialize progress for all projects in projectSettings
    Object.keys(projectSettings).forEach(projectName => {
      progress[projectName] = { points: 0, volume: 0, costs: 0, fees: 0, customMetric: 0 };
    });
    
    // Sum up entries for the current week
    entries.forEach(entry => {
      const projectName = entry.project;
      
      // Find matching project (case-insensitive partial match)
      const matchedProject = Object.keys(projectSettings).find(p => 
        projectName.toLowerCase().includes(p.toLowerCase()) ||
        p.toLowerCase().includes(projectName.toLowerCase())
      );
      
      if (matchedProject) {
        progress[matchedProject].points += entry.points || 0;
        progress[matchedProject].volume += entry.volume || 0;
        progress[matchedProject].costs += entry.cost || 0;
        progress[matchedProject].fees += entry.fees || 0;
        progress[matchedProject].customMetric += entry.customMetric || 0;
      } else if (progress[projectName]) {
        // Exact match
        progress[projectName].points += entry.points || 0;
        progress[projectName].volume += entry.volume || 0;
        progress[projectName].costs += entry.cost || 0;
        progress[projectName].fees += entry.fees || 0;
        progress[projectName].customMetric += entry.customMetric || 0;
      } else {
        // New project not in settings, add it
        progress[projectName] = {
          points: entry.points || 0,
          volume: entry.volume || 0,
          costs: entry.cost || 0,
          fees: entry.fees || 0,
          customMetric: entry.customMetric || 0
        };
      }
    });
    
    return progress;
  }, [weeklyData, projectSettings]);

  // ROI calculation based on project settings
  const roiCalculation = useMemo(() => {
    const roi = { bearish: 0, base: 0, bullish: 0 };
    
    Object.entries(projectSettings).forEach(([projectName, settings]) => {
      const points = getTotalPoints(weeklyData, projectName);
      
      if (settings?.roiScenarios) {
        roi.bearish += points * (settings.roiScenarios.bearish || 0);
        roi.base += points * (settings.roiScenarios.base || 0);
        roi.bullish += points * (settings.roiScenarios.bullish || 0);
      }
    });
    
    return roi;
  }, [weeklyData, projectSettings]);

  return {
    stats,
    weeklyProgress,
    roiCalculation
  };
};